import { Component, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { RevenueItemService } from 'src/app/revenue/shared/revenue-items/revenue-item.service';
import { EventCustomRegField } from '../../shared/custom-reg-fields/event-custom-reg-field.model';
import { RegistrationCustomRegFieldResponse } from '../../shared/registration/registration-custom-reg-field-response.model';
import { EventFieldVisibleRequiredEnum } from '../../shared/event-field-visible-required.enum';
import { IValidationComponent } from 'src/app/shared/widgets/component/ivalidation.component';
import { ValidationResult } from 'src/app/shared/widgets/validators/validation-result.model';
import { EventV4RegistrationCustomFieldComponent } from './event-v4-custom-field/event-v4-registration-custom-field.component';
import { EventRegistrationActionTrackerService } from '../../shared/event-registration-action-tracker.service';
import { EventRegistationActionTypeEnum } from '../../shared/dto/event-registation-action-type.enum';
import { EventRegistrationEditActionDto } from '../../shared/dto/event-registration-edit-action.dto';
import { UtilService } from 'src/app/shared';

@Component({
  selector: 'wl-event-v4-registration-custom-reg-fields',
  styleUrls: ['event-v4-registration-custom-fields.component.css'],
  templateUrl: 'event-v4-registration-custom-fields.component.html',
    standalone: false
})
export class EventV4RegistrationCustomFieldsComponent implements IValidationComponent {
  /**
   * Responses to the custom fields
   */
  @Input() public get responses(): Array<RegistrationCustomRegFieldResponse> {
    return this._responses;
  }
  public set responses(value: Array<RegistrationCustomRegFieldResponse>) {
    this._responses = value;

    this._originalGivenResponses = this.utilService.clone(value);

    this.setupCustomFields();
  }

  /*
   * custom fields to show
   */
  @Input() public get customFields(): Array<EventCustomRegField> {
    return this._customFields;
  }
  public set customFields(value: Array<EventCustomRegField>) {
    this._customFields = value;
    this.setupCustomFields();
  }

  /*
   * which custom field types to show, IE 1 = Registration... etc
   */
  @Input() public get visibilityFilter(): Array<EventFieldVisibleRequiredEnum> {
    return this._customFieldVisibility;
  }
  public set visibilityFilter(value: Array<EventFieldVisibleRequiredEnum>) {
    this._customFieldVisibility = value;
    this.setupCustomFields();
  }

  /*
   * which event items for which to show custom fields
   */
  @Input() public get eventItemIds(): Array<number> {
    return this._eventItemIds;
  }
  public set eventItemIds(value: Array<number>) {
    this._eventItemIds = value;
    this.setupCustomFields();
  }

  @Output() public responseChange = new EventEmitter<RegistrationCustomRegFieldResponse>();

  @ViewChildren(EventV4RegistrationCustomFieldComponent) public _customFieldViews: QueryList<EventV4RegistrationCustomFieldComponent>;

  public customFieldsAndResponses: Array<{ customField: EventCustomRegField; response: RegistrationCustomRegFieldResponse }> = [];
  public _responses: Array<RegistrationCustomRegFieldResponse> = [];
  public _originalGivenResponses: Array<RegistrationCustomRegFieldResponse> = [];
  public _customFields: Array<EventCustomRegField> = [];
  public _customFieldVisibility: Array<EventFieldVisibleRequiredEnum> = [];
  public _eventItemIds: Array<number> = [];

  public constructor(
    private _revenueItemService: RevenueItemService,
    private actionTrackerService: EventRegistrationActionTrackerService,
    private utilService: UtilService
  ) {}

  /**
   * re-applies filters to visible custom fields, and creates/retrieves-old responses
   */
  public setupCustomFields(): void {
    if (!this._customFields) this._customFields = [];

    this.customFieldsAndResponses = this._customFields
      .filter((customField) => this._eventItemIds.some((eventItemId) => customField.EventItemIds.indexOf(eventItemId) >= 0))
      .filter((customField) => this._customFieldVisibility.indexOf(customField.Visible) >= 0)
      .map((customField) => {
        return {
          customField: customField,
          response: this.getOrCreateResponseForCustomField(customField),
        };
      })
      .filter((x) => !!x.response);

    setTimeout(() => {
      while (this._responses.length) this._responses.pop();

      this.customFieldsAndResponses.map((x) => x.response).forEach((r) => this._responses.push(r));
    });
  }

  /**
   * triggered when the response changes
   * @param response
   */
  public onResponseChange(response: RegistrationCustomRegFieldResponse): void {
    if (response.CustomField.ItemNum > 0 && response.CustomField.AvailableValues && response.CustomField.AvailableValues.length) {
      let originalResponse = this._originalGivenResponses.find((x) => x.EventCustomRegFieldId == response.CustomField.Id);
      let originalValue = originalResponse?.CustomField?.AvailableValues?.find(
        (x) => x.Id == originalResponse?.EventCustomRegFieldAvailValuesId
      );
      const selectedValue = response.CustomField.AvailableValues.find((av) => av.Id == response.EventCustomRegFieldAvailValuesId);
      this.actionTrackerService.addAction({
        Type: EventRegistationActionTypeEnum.CustomFieldCostChanged,
        OldValue: originalValue?.Amount ?? 0,
        NewValue: selectedValue.Amount,
        KeyValue: `${response?.AttendeeHeaderId}, ${response?.RegistrationHeaderId}, ${response?.CustomField.Id}, ${originalResponse?.ResponseId}`,
      } as EventRegistrationEditActionDto);
      this._revenueItemService.getItemTaxAmount(response.CustomField.ItemNum, selectedValue.Amount).then((data) => {
        selectedValue.TaxAmount = data;
        this.responseChange.emit(response);
      });
    } else {
      this.responseChange.emit(response);
    }
  }

  public getOrCreateResponseForCustomField(customField: EventCustomRegField): RegistrationCustomRegFieldResponse {
    let response: RegistrationCustomRegFieldResponse = null;

    // try to find existing response first
    if (this._originalGivenResponses) {
      let existingResponse: RegistrationCustomRegFieldResponse = this._originalGivenResponses.find(
        (resp) => resp.EventCustomRegFieldId == customField.Id
      );

      if (existingResponse) response = existingResponse;
    }

    // if no existing response, create an empty one
    if (response == null) {
      response = new RegistrationCustomRegFieldResponse();
      this._originalGivenResponses.push(this.utilService.clone(response));
    }

    if (response.CustomField == null) response.CustomField = JSON.parse(JSON.stringify(customField));

    response.CustomField.Amount = customField.Amount;
    response.CustomField.AvailableValues = customField.AvailableValues;
    response.CustomField.Descr = customField.Descr;
    response.CustomField.DisplayOrder = customField.DisplayOrder;
    response.CustomField.EventId = customField.EventId;
    response.CustomField.EventItemIds = customField.EventItemIds;
    response.CustomField.FieldName = customField.FieldName;
    response.CustomField.Required = customField.Required;
    response.CustomField.StaffOnly = customField.StaffOnly;
    response.CustomField.UiControlType = customField.UiControlType;
    response.CustomField.Id = response.EventCustomRegFieldId = customField.Id;
    response.CustomField.Visible = customField.Visible;
    response.CustomField.ItemNum = customField.ItemNum;

    return this.utilService.clone(response);
  }

  /**
   * let anyone who asks know if all our fields are valid
   */
  public getValidationResult(): ValidationResult {
    let validation: ValidationResult = new ValidationResult();
    validation.isValid = true;

    if (this._customFieldViews) {
      validation.isValid = this._customFieldViews.map((x) => x.getValidationResult()).every((x) => x.isValid);
      if (!validation.isValid) validation.errors.push();
    }

    return validation;
  }
}
